import { useCallback, useEffect, useState } from "react";
import firebase from "../../configs/FirebaseConfig";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { message } from "antd";
import i18n from "i18next";

const db = firebase.firestore();
const storage = firebase.storage();

const collectionName = "productColors";

/**
 * Hook to fetch and manage product colors from Firestore
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoFetch - Whether to automatically fetch data on mount (default: true)
 * @param {boolean} options.realtime - Whether to use real-time listener (default: true)
 * @param {boolean} options.activeOnly - Fetch only active colors (default: false)
 * @returns {Object} - Colors data and utility functions
 */
export const useProductColors = (options = {}) => {
  const { autoFetch = true, realtime = true, activeOnly = false } = options;
  const auth = useSelector((state) => state.firebaseReducer.auth);

  const [colors, setColors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch all product colors
   * @returns {Promise<Array>} - Array of color documents
   */
  const fetchColors = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      let query = db.collection(collectionName);

      if (activeOnly) {
        query = query.where("active", "==", true);
      }

      const snapshot = await query.get();

      if (snapshot.empty) {
        setColors([]);
        return [];
      }

      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setColors(data);
      return data;
    } catch (err) {
      console.error("Error fetching product colors:", err);
      setError(err);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [activeOnly]);

  /**
   * Add a new product color
   * @param {Object} colorData - Color data to add
   * @param {string} colorData.name - Color name
   * @param {string} colorData.code - Color code
   * @param {File} colorData.imageFile - Image file to upload (optional)
   * @param {string} colorData.image - Image URL (optional, if not uploading file)
   * @returns {Promise<Object>} - Created color document
   */
  const addColor = useCallback(
    async (colorData) => {
      if (!auth.email) {
        throw new Error("User not authenticated");
      }

      setIsLoading(true);
      setError(null);

      try {
        let imageUrl = colorData.image || null;

        // Upload image to Firebase Storage if file is provided
        if (colorData.imageFile) {
          const file = colorData.imageFile;
          const fileName = `${Date.now()}_${file.name}`;
          const storageRef = storage.ref(`product-colors/${fileName}`);

          await storageRef.put(file);
          imageUrl = await storageRef.getDownloadURL();
        }

        const newColor = {
          name: colorData.name,
          code: colorData.code,
          image: imageUrl,
          shadeId: colorData.shadeId || null,
          active: colorData.active !== undefined ? colorData.active : true,
          createdAt: dayjs().unix(),
          createdBy: auth.email,
          updatedAt: dayjs().unix(),
          updatedBy: auth.email,
        };

        const docRef = await db.collection(collectionName).add(newColor);
        const createdColor = { id: docRef.id, ...newColor };

        message.success(i18n.t("notifications.color_added"));
        return createdColor;
      } catch (err) {
        console.error("Error adding product color:", err);
        setError(err);
        message.error(i18n.t("notifications.upload_failed"));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [auth.email],
  );

  /**
   * Update an existing product color
   * @param {string} colorId - Color document ID
   * @param {Object} colorData - Updated color data
   * @param {File} colorData.imageFile - New image file to upload (optional)
   * @returns {Promise<Object>} - Updated color document
   */
  const updateColor = useCallback(
    async (colorId, colorData) => {
      if (!auth.email) {
        throw new Error("User not authenticated");
      }

      setIsLoading(true);
      setError(null);

      try {
        const colorRef = db.collection(collectionName).doc(colorId);
        const colorDoc = await colorRef.get();

        if (!colorDoc.exists) {
          throw new Error("Color not found");
        }

        const existingData = colorDoc.data();
        let imageUrl = colorData.image !== undefined ? colorData.image : existingData.image;

        // Upload new image if file is provided
        if (colorData.imageFile) {
          // Delete old image if exists
          if (existingData.image && existingData.image.includes("firebase")) {
            try {
              const oldImageRef = storage.refFromURL(existingData.image);
              await oldImageRef.delete();
            } catch (err) {
              console.warn("Could not delete old image:", err);
            }
          }

          // Upload new image
          const file = colorData.imageFile;
          const fileName = `${Date.now()}_${file.name}`;
          const storageRef = storage.ref(`product-colors/${fileName}`);

          await storageRef.put(file);
          imageUrl = await storageRef.getDownloadURL();
        }

        const updatedData = {
          name: colorData.name !== undefined ? colorData.name : existingData.name,
          code: colorData.code !== undefined ? colorData.code : existingData.code,
          image: imageUrl,
          shadeId: colorData.shadeId !== undefined ? colorData.shadeId : existingData.shadeId,
          active: colorData.active !== undefined ? colorData.active : existingData.active,
          updatedAt: dayjs().unix(),
          updatedBy: auth.email,
        };

        await colorRef.update(updatedData);

        const updatedColor = { id: colorId, ...existingData, ...updatedData };
        message.success(i18n.t("notifications.color_updated"));
        return updatedColor;
      } catch (err) {
        console.error("Error updating product color:", err);
        setError(err);
        message.error(i18n.t("notifications.upload_failed"));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [auth.email],
  );

  /**
   * Delete a product color
   * @param {string} colorId - Color document ID
   * @returns {Promise<void>}
   */
  const deleteColor = useCallback(
    async (colorId) => {
      if (!auth.email) {
        throw new Error("User not authenticated");
      }

      setIsLoading(true);
      setError(null);

      try {
        const colorRef = db.collection(collectionName).doc(colorId);
        const colorDoc = await colorRef.get();

        if (!colorDoc.exists) {
          throw new Error("Color not found");
        }

        const colorData = colorDoc.data();

        // Delete image from storage if exists
        if (colorData.image && colorData.image.includes("firebase")) {
          try {
            const imageRef = storage.refFromURL(colorData.image);
            await imageRef.delete();
          } catch (err) {
            console.warn("Could not delete image:", err);
          }
        }

        await colorRef.delete();
        message.success(i18n.t("notifications.color_deleted"));
      } catch (err) {
        console.error("Error deleting product color:", err);
        setError(err);
        message.error(i18n.t("notifications.delete_failed"));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [auth.email],
  );

  /**
   * Get color by code
   * @param {string} code - Color code
   * @returns {Object|null} - Color object or null
   */
  const getColorByCode = useCallback(
    (code) => {
      return colors.find((color) => color.code === code) || null;
    },
    [colors],
  );

  /**
   * Get active colors only
   * @returns {Array} - Array of active colors
   */
  const getActiveColors = useCallback(() => {
    return colors.filter((color) => color.active === true);
  }, [colors]);

  // Set up real-time listener or fetch once
  useEffect(() => {
    if (!autoFetch) return;

    if (auth.isEmpty) {
      setColors([]);
      return;
    }

    if (realtime) {
      // Real-time listener
      setIsLoading(true);
      let query = db.collection(collectionName);

      if (activeOnly) {
        query = query.where("active", "==", true);
      }

      const unsubscribe = query.onSnapshot(
        (snapshot) => {
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setColors(data);
          setIsLoading(false);
          setError(null);
        },
        (err) => {
          console.error("useProductColors: Error in listener:", err);
          setError(err);
          setColors([]);
          setIsLoading(false);
        },
      );

      return () => {
        if (unsubscribe) unsubscribe();
      };
    } else {
      // One-time fetch
      fetchColors();
    }
  }, [autoFetch, realtime, activeOnly, fetchColors, auth.isEmpty]);

  return {
    colors,
    isLoading,
    error,
    fetchColors,
    addColor,
    updateColor,
    deleteColor,
    getColorByCode,
    getActiveColors,
  };
};
